export interface PlayOptions {
    wpm: number;
    frequency: number;
    gain?: number;
}

export interface ScheduledSymbol {
    kind: 'dot' | 'dash';
    startMs: number;
    endMs: number;
}

export interface PlaybackSchedule {
    symbols: ScheduledSymbol[];
    totalMs: number;
}

export interface PlaybackHandle {
    schedule: PlaybackSchedule;
    duration: number;
    stop: () => void;
}

export function computeSchedule(morse: string, wpm: number): PlaybackSchedule {
    const unitMs = (1.2 / wpm) * 1000;
    const symbols: ScheduledSymbol[] = [];
    let t = 0;

    const words = morse.split(/\s*\/\s*/);
    words.forEach((word, wIdx) => {
        const letters = word.trim().split(/\s+/).filter(Boolean);
        letters.forEach((letter, lIdx) => {
            const syms = letter.split('').filter((s) => s === '.' || s === '-');
            syms.forEach((sym, sIdx) => {
                const dur = sym === '.' ? unitMs : unitMs * 3;
                symbols.push({
                    kind: sym === '.' ? 'dot' : 'dash',
                    startMs: t,
                    endMs: t + dur,
                });
                t += dur;
                if (sIdx < syms.length - 1) t += unitMs;
            });
            if (lIdx < letters.length - 1) t += unitMs * 3;
        });
        if (wIdx < words.length - 1) t += unitMs * 7;
    });

    return { symbols, totalMs: t };
}

export function playMorse(ctx: AudioContext, morse: string, options: PlayOptions): PlaybackHandle {
    const { frequency, gain = 0.15 } = options;
    const schedule = computeSchedule(morse, options.wpm);

    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.frequency.value = frequency;
    osc.type = 'sine';
    g.gain.value = 0;
    osc.connect(g);
    g.connect(ctx.destination);

    const startAt = ctx.currentTime + 0.05;
    schedule.symbols.forEach((sym) => {
        const onAt = startAt + sym.startMs / 1000;
        const offAt = startAt + sym.endMs / 1000;
        g.gain.setValueAtTime(gain, onAt);
        g.gain.setValueAtTime(0, offAt);
    });

    osc.start(startAt);
    osc.stop(startAt + schedule.totalMs / 1000 + 0.1);

    return {
        schedule,
        duration: schedule.totalMs / 1000,
        stop: () => {
            try { g.gain.cancelScheduledValues(ctx.currentTime); } catch {}
            try { g.gain.setValueAtTime(0, ctx.currentTime); } catch {}
            try { osc.stop(); } catch {}
            try { osc.disconnect(); } catch {}
            try { g.disconnect(); } catch {}
        },
    };
}
